import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button, Input, Card, CardContent, Label, Textarea } from '@/components/ui'
import { Building2, ArrowLeft } from 'lucide-react'
import { api } from '@/api/client'

export function CreateOrgPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
  })

  // Auto-generate slug from name
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50)
    setFormData({ ...formData, name, slug })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.slug.trim()) {
      setError('Name and slug are required')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await api.orgs.create({
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: formData.description.trim() || undefined,
      })
      // Navigate to the new org's dashboard
      navigate(`/orgs/${response.org.slug}`)
    } catch (err) {
      console.error('Failed to create organization:', err)
      setError(err instanceof Error ? err.message : 'Failed to create organization')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>

      <PageHeader
        title="Create Organization"
        description="Organizations are the top-level container for your apps and team"
      />

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--bg-elevated-2)]">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--bg-elevated-3)]">
                <Building2 className="h-8 w-8 text-[var(--text-secondary)]" />
              </div>
              <div>
                <h3 className="font-medium text-[var(--text-primary)]">
                  {formData.name || 'New Organization'}
                </h3>
                <p className="text-sm text-[var(--text-muted)]">
                  {formData.slug || 'organization-slug'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  placeholder="Acme Inc"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">
                  Slug
                  <span className="ml-1 text-[var(--text-muted)] font-normal">
                    (used in URLs)
                  </span>
                </Label>
                <Input
                  id="slug"
                  placeholder="acme-inc"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  pattern="^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$"
                  title="Slug must contain only lowercase letters, numbers, and hyphens"
                  required
                />
                <p className="text-xs text-[var(--text-muted)]">
                  Only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  Description
                  <span className="ml-1 text-[var(--text-muted)] font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="description"
                  placeholder="What does your organization do?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border-subtle)]">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/')}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Organization'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
